import { useMemo, useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { addToDiscord } from "../utils/discord";

const API_BASE = import.meta.env.VITE_API_URL || "";

function formatCost(cost) {
  if (typeof cost === "string") cost = parseFloat(cost);
  return `$${(Number.isFinite(cost) ? cost : 0).toFixed(4)}`;
}

export default function StatsSummary() {
  const {
    apiKeys,
    models,
    selectedMonth,
    setSelectedMonth,
    monthlyUsageData,
    loadingMonthlyData,
    showToast,
    creditStatus,
    fetchMonthUsage,
    trackEvent
  } = useApp();

  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState(null);
  const [checkingBilling, setCheckingBilling] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // 'setup' | 'upgrade' | null
  const [activeBanner, setActiveBanner] = useState(null);

  useEffect(() => {
    // Check billing status to show upgrade banner
    fetch(`${API_BASE}/api/billing/status`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => setBillingStatus(data))
      .finally(() => setCheckingBilling(false));
  }, []);

  // Unified Logic: Determine which banner to show (Activation -> Honeymoon -> Upgrade)
  useEffect(() => {
    // Wait for data to load
    if (loadingMonthlyData || checkingBilling || !monthlyUsageData) return;

    const totalRequests = monthlyUsageData?.current_month?.total_requests || 0;
    let activatedAt = parseInt(localStorage.getItem("ob_activated_at") || "0", 10);

    // 1. If no usage yet, show Setup Guide (Activation Phase) - EVEN FOR PREMIUM
    if (activatedAt === 0 && totalRequests === 0) {
      setActiveBanner("setup");
      return;
    }

    // 2. If Premium (and active), show nothing. (They know what they are doing).
    if (billingStatus?.is_premium) {
      setActiveBanner(null);
      return;
    }

    // 3. User is active. Check/Start Honeymoon Timer.
    const now = Date.now();

    if (!activatedAt) {
      // First time we've seen them with usage > 0. Start the clock now.
      activatedAt = now;
      localStorage.setItem("ob_activated_at", String(activatedAt));
    }

    // 4. Check Upgrade logic
    const snoozeUntil = parseInt(localStorage.getItem("ob_upgrade_snooze_until") || "0", 10);
    const honeymoonOver = now - activatedAt >= 3 * 24 * 60 * 60 * 1000; // 3 days since first message
    // const honeymoonOver = now - activatedAt >= 2 * 60 * 1000; // 2 minutes since first message
    // const honeymoonOver = true; // debug

    if (honeymoonOver && now > snoozeUntil) {
      setActiveBanner("upgrade");
    } else {
      setActiveBanner(null);
    }
  }, [monthlyUsageData, loadingMonthlyData, checkingBilling, billingStatus]);

  async function handleUpgrade(plan) {
    if (upgrading) return;
    trackEvent("upgrade_checkout_start", { plan, source: "stats_summary" });
    setUpgrading(true);
    setShowUpgradeModal(false);
    try {
      const res = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || "Failed to start checkout");
      }
      window.location = data.url;
    } catch (e) {
      showToast(e.message || "Failed to start checkout", "error");
      setUpgrading(false);
    }
  }

  const totalModels = useMemo(() => {
    if (!Array.isArray(models)) return 0;
    return models.reduce((acc, m) => acc + (m.models?.length || 0), 0);
  }, [models]);

  // Determine if we're viewing current month
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const isCurrentMonth = !selectedMonth || selectedMonth === currentMonth;

  // Get the cost to display from monthlyUsageData
  const displayCost = monthlyUsageData?.current_month?.total_cost || 0;

  // Generate label for the cost stat
  const costLabel = useMemo(() => {
    if (isCurrentMonth) {
      return "This Month's Cost";
    }

    if (selectedMonth) {
      const [year, month] = selectedMonth.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString("en-US", { month: "long", year: "numeric" }) + " Cost";
    }

    return "This Month's Cost";
  }, [isCurrentMonth, selectedMonth]);

  function handleResetToCurrentMonth() {
    setSelectedMonth(currentMonth);
    fetchMonthUsage(currentMonth);
  }


  // Ensure we have current-month data loaded when this mounts
  useEffect(() => {
    if (!monthlyUsageData) {
      fetchMonthUsage(selectedMonth || currentMonth);
    }
  }, [monthlyUsageData, fetchMonthUsage, selectedMonth, currentMonth]);

  return (
    <>
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content upgrade-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Choose Your Plan</h2>
              <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="upgrade-plan-cards">
                {/* Monthly Plan */}
                <div className="upgrade-plan-card">
                  <div className="upgrade-plan-header">
                    <h3>Monthly</h3>
                    <div className="upgrade-plan-price">
                      <span className="upgrade-price-currency">$</span>
                      <span className="upgrade-price-amount">3.99</span>
                      <span className="upgrade-price-period">/month</span>
                    </div>
                    <p className="upgrade-plan-desc">Billed monthly</p>
                  </div>
                  <ul className="upgrade-plan-features">
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Unlimited messages
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Code/text attachments support
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      15000 monthly credits for built-in models
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      All supported providers
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Works with your own API keys
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      0% markup on your own API usage
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Custom model presets
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Usage tracking
                    </li>
                  </ul>
                  <button
                    className="btn btn-secondary btn-full"
                    onClick={() => handleUpgrade("premium_monthly")}
                    disabled={upgrading}
                  >
                    {upgrading ? "Starting..." : "Choose Monthly"}
                  </button>
                </div>

                {/* Yearly Plan */}
                <div className="upgrade-plan-card upgrade-plan-featured">
                  <div className="upgrade-featured-badge">Save 25%</div>
                  <div className="upgrade-plan-header">
                    <h3>Yearly</h3>
                    <div className="upgrade-plan-price">
                      <span className="upgrade-price-currency">$</span>
                      <span className="upgrade-price-amount">2.99</span>
                      <span className="upgrade-price-period">/month</span>
                    </div>
                    <p className="upgrade-plan-desc">Billed as $35.88/year</p>
                  </div>
                  <ul className="upgrade-plan-features">
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Unlimited messages
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Code/text attachments support
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      15000 monthly credits for built-in models
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      All supported providers
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Works with your own API keys
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      0% markup on your own API usage
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Custom model presets
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Usage tracking
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      <strong>Save $12/year</strong>
                    </li>
                  </ul>
                  <button
                    className="btn btn-primary btn-full"
                    onClick={() => handleUpgrade("premium_yearly")}
                    disabled={upgrading}
                  >
                    {upgrading ? "Starting..." : "Choose Yearly"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Setup Prompt for New Users */}
      {activeBanner === "setup" && (
        <div className="setup-prompt-banner">
          <div className="setup-prompt-icon">
            <svg width={32} height={32} fill="#5865F2" viewBox="0 0 24 24">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
          </div>
          <div className="setup-prompt-content">
            <div className="setup-prompt-title">
              Welcome! Let's get you started with OkeyBot
            </div>
            <div className="setup-prompt-description">
              Invite OkeyBot to your personal server for private use, or use it in any server where the bot is present.
              Your API keys and settings follow your account everywhere.
            </div>
          </div>
          <button
            className="btn btn-primary setup-prompt-button"
            onClick={addToDiscord}
            data-umami-event="add_to_discord_dashboard_setup"
          >
            <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
            Add to Discord
          </button>
        </div>
      )}

      {/* Upgrade Banner for Free Users (delayed) */}
      {activeBanner === "upgrade" && (
        <div style={{
          background: "linear-gradient(135deg, rgba(20, 184, 166, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)",
          border: "1px solid rgba(20, 184, 166, 0.3)",
          borderRadius: "0.75rem",
          padding: "1.25rem 1.5rem",
          marginBottom: "2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "1rem",
          animation: "slideDown 0.3s ease-out"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", flex: "1", minWidth: "250px" }}>
            <div style={{
              background: "var(--accent)",
              borderRadius: "50%",
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              <svg width={24} height={24} fill="white" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "600", fontSize: "1.125rem", color: "var(--text-primary)", marginBottom: "0.25rem" }}>
                20 messages/day • Upgrade for unlimited
              </div>
              <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                Get unlimited messages for $3.99/month or $2.99/month billed yearly
              </div>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowUpgradeModal(true)}
            disabled={upgrading}
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "0.9375rem",
              fontWeight: "600",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
          >
            <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            {upgrading ? "Starting..." : "Upgrade Now"}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              const until = Date.now() + 7 * 24 * 60 * 60 * 1000; // snooze 7 days
              localStorage.setItem("ob_upgrade_snooze_until", String(until));
              setActiveBanner(null)
            }}
            disabled={upgrading}
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "0.9375rem",
              fontWeight: "600",
              whiteSpace: "nowrap",
              marginLeft: "0.5rem"
            }}
          >
            Not now
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">API Keys</div>
          <div className="stat-value">
            {apiKeys === null ? "\u00A0" : apiKeys.length}
          </div>
          <div className="stat-help">Active providers</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Available Models</div>
          <div className="stat-value">
            {models === null ? "\u00A0" : totalModels}
          </div>
          <div className="stat-help">Across all providers</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Okey Credits</div>
          <div className="stat-value">
            {creditStatus ? creditStatus.balance : "\u00A0"}
          </div>
          <div className="stat-help">
            For built-in OkeyBot models
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ position: "relative" }}>
            {costLabel}
            {!isCurrentMonth && (
              <button
                onClick={handleResetToCurrentMonth}
                className="reset-to-current-button"
                onMouseEnter={(e) => e.target.style.opacity = "1"}
                onMouseLeave={(e) => e.target.style.opacity = "0.7"}
                title="Back to current month"
              >
                <svg width={14} height={14} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
                </svg>
              </button>
            )}
          </div>
          <div className="stat-value" style={{ color: "#f59e0b" }}>
            {loadingMonthlyData ? (
              <div style={{
                background: 'var(--bg-secondary)',
                height: '32px',
                width: '100px',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
                margin: '0 auto'
              }} />
            ) : (
              formatCost(displayCost)
            )}
          </div>
          <div className="stat-help">
            {isCurrentMonth ? "Current API spending" : "Total API spending"}
          </div>
        </div>
      </div>
    </>
  );
}
